/** Device enumeration for the pre-join check and the in-meeting device menu. */
import { useCallback, useEffect, useState } from 'react';
import type { DeviceInfo, DeviceKind } from '../media/contract';

export interface DeviceLists {
  readonly cameras: readonly DeviceInfo[];
  readonly microphones: readonly DeviceInfo[];
  readonly speakers: readonly DeviceInfo[];
  readonly permissionDenied: boolean;
  refresh(): void;
}

const KIND: Record<DeviceKind, MediaDeviceKind> = {
  videoinput: 'videoinput',
  audioinput: 'audioinput',
  audiooutput: 'audiooutput',
};

export function useDevices(): DeviceLists {
  const [devices, setDevices] = useState<readonly DeviceInfo[]>([]);
  const [permissionDenied, setPermissionDenied] = useState(false);

  const refresh = useCallback(() => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    navigator.mediaDevices
      .enumerateDevices()
      .then((list) =>
        setDevices(
          list.map((d, index) => ({
            deviceId: d.deviceId,
            label: d.label || `${d.kind} ${index + 1}`,
            kind: d.kind,
          })),
        ),
      )
      .catch(() => setPermissionDenied(true));
  }, []);

  useEffect(() => {
    refresh();
    navigator.mediaDevices?.addEventListener?.('devicechange', refresh);
    return () => navigator.mediaDevices?.removeEventListener?.('devicechange', refresh);
  }, [refresh]);

  const byKind = (kind: DeviceKind): readonly DeviceInfo[] =>
    devices.filter((d) => d.kind === KIND[kind]);

  return {
    cameras: byKind('videoinput'),
    microphones: byKind('audioinput'),
    speakers: byKind('audiooutput'),
    permissionDenied,
    refresh,
  };
}
